import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  ApplicationCreateDraft,
  CreateOpenIddictApplicationDto,
  IOpenIddictApplication,
  UpdateOpenIddictApplicationDto,
} from '~/Services/OpenIddictApplication.interface'
import { ModalName } from './constants'

interface ModalVisibility {
  name: string
  visible: boolean
}

interface OpenIddictApplicationState {
  isLoading: boolean
  isConfirmLoading: boolean
  isLogoSaving: boolean
  applications: IOpenIddictApplication[]
  applicationSelected?: IOpenIddictApplication
  applicationCreateDraft?: ApplicationCreateDraft
  modalVisibility: {
    [id: string]: { visible: boolean }
  }
}

const initialState: OpenIddictApplicationState = {
  isLoading: false,
  isConfirmLoading: false,
  isLogoSaving: false,
  applications: [],
  modalVisibility: {},
}

export const OpenIddictApplicationSlice = createSlice({
  name: 'openIddictApplication',
  initialState,
  reducers: {
    getApplications: state => {
      state.isLoading = true
    },
    getApplicationsSuccess: (
      state,
      action: PayloadAction<IOpenIddictApplication[]>,
    ) => {
      state.applications = action.payload
      state.isLoading = false
    },
    getApplicationsFail: state => {
      state.isLoading = false
    },
    setModalVisibility: (state, action: PayloadAction<ModalVisibility>) => {
      const { name, visible } = action.payload
      state.modalVisibility[name] = { visible }
    },
    setApplicationSelected: (
      state,
      action: PayloadAction<IOpenIddictApplication | undefined>,
    ) => {
      state.applicationSelected = action.payload
    },
    setApplicationCreateDraft: (
      state,
      action: PayloadAction<ApplicationCreateDraft | undefined>,
    ) => {
      state.applicationCreateDraft = action.payload
    },
    createApplication: (
      state,
      action: PayloadAction<CreateOpenIddictApplicationDto>,
    ) => {
      state.isConfirmLoading = true
    },
    createApplicationSuccess: state => {
      state.isConfirmLoading = false
      state.modalVisibility[ModalName.Application] = { visible: false }
      // Only a real create clears the draft — closing the modal without saving must not lose it.
      state.applicationCreateDraft = undefined
    },
    createApplicationFail: state => {
      state.isConfirmLoading = false
    },
    updateApplication: (
      state,
      action: PayloadAction<{ id: string; data: UpdateOpenIddictApplicationDto }>,
    ) => {
      state.isConfirmLoading = true
    },
    updateApplicationSuccess: state => {
      state.isConfirmLoading = false
      state.modalVisibility[ModalName.Application] = { visible: false }
    },
    updateApplicationFail: state => {
      state.isConfirmLoading = false
    },
    deleteApplication: (state, action: PayloadAction<string>) => {
      state.isConfirmLoading = true
    },
    deleteApplicationSuccess: state => {
      state.isConfirmLoading = false
    },
    deleteApplicationFail: state => {
      state.isConfirmLoading = false
    },
    uploadLogo: (state, action: PayloadAction<{ id: string; file: File }>) => {
      state.isLogoSaving = true
    },
    removeLogo: (state, action: PayloadAction<string>) => {
      state.isLogoSaving = true
    },
    // Cả upload lẫn remove đều trả về application đầy đủ (id + logoUrl mới) — patch tại chỗ thay vì
    // getApplications() lại toàn bộ danh sách, giữ modal đang mở không bị nháy/mất state khác.
    logoActionSuccess: (state, action: PayloadAction<IOpenIddictApplication>) => {
      state.isLogoSaving = false
      const updated = action.payload
      if (state.applicationSelected?.id === updated.id) {
        state.applicationSelected = updated
      }
      const index = state.applications.findIndex(a => a.id === updated.id)
      if (index !== -1) {
        state.applications[index] = updated
      }
    },
    logoActionFail: state => {
      state.isLogoSaving = false
    },
  },
})

export const OpenIddictApplicationActions = OpenIddictApplicationSlice.actions
export const OpenIddictApplicationReducer = OpenIddictApplicationSlice.reducer
